/**
 * Il catalogo dei corsi che eroghiamo noi, con le edizioni in calendario.
 *
 * È il listino di Sinthema, uguale per tutti i clienti: sta qui e non dentro
 * la scheda di un cliente, perché i corsi sono i nostri e le date pure.
 * Da questo catalogo nascono le proposte che accompagnano le comunicazioni
 * di scadenza: un corso senza data resta un'intenzione, con la data diventa
 * un appuntamento.
 */
import { useEffect, useState } from 'react';
import { formazioneApi } from '../api';
import CampoImporto from './CampoImporto';

const oggi = () => new Date().toISOString().slice(0, 10);
const fmtData = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const STATI = [['prevista', 'Prevista'], ['confermata', 'Confermata'],
               ['svolta', 'Svolta'], ['annullata', 'Annullata']];

export default function NostriCorsi() {
  const [corsi, setCorsi] = useState([]);
  const [sessioni, setSessioni] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [tutte, setTutte] = useState(false);

  const carica = () => Promise.all([
    formazioneApi.catalogo().then(setCorsi),
    formazioneApi.sessioni(tutte).then(setSessioni),
  ]).catch(e => alert(e?.error || e));
  useEffect(() => { carica(); }, [tutte]);   // eslint-disable-line react-hooks/exhaustive-deps

  const setCorso = (i, k, v) => setCorsi(c => c.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const setSess = (i, k, v) => setSessioni(s => s.map((x, j) => j === i ? { ...x, [k]: v } : x));

  const salvaCorsi = async () => {
    setSalvando(true);
    try { await formazioneApi.salvaCatalogo(corsi.filter(c => c.nome?.trim())); await carica(); }
    catch (e) { alert(e?.error || e); } finally { setSalvando(false); }
  };
  const salvaSessioni = async () => {
    setSalvando(true);
    try { await formazioneApi.salvaSessioni(sessioni.filter(s => s.catalogo_id && s.data), tutte); await carica(); }
    catch (e) { alert(e?.error || e); } finally { setSalvando(false); }
  };

  // Quante edizioni future ha ogni corso: un corso senza date non si può
  // proporre con un appuntamento, e conviene vederlo dall'elenco.
  const futureDi = (id) => sessioni.filter(
    s => String(s.catalogo_id) === String(id) && new Date(s.data) >= new Date(oggi())
      && s.stato !== 'annullata').length;

  return (
    <div className="space-y-5">
      <div className="card space-y-3">
        <div>
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            I corsi che eroghiamo
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Il nostro listino, uguale per tutti i clienti. Due prezzi perché si vende in due modi:
            a persona per chi ne manda una o due, a sessione quando si forma un'aula in azienda.
            Per far comparire un corso nelle proposte, abbinalo al corso del cliente in
            <strong> Corsi previsti</strong>, colonna “Nostro corso abbinato”. Un corso che non
            eroghiamo più si archivia con la ✕: resta qui spento, con il suo storico, e si
            rimette a catalogo con la ↺.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="pb-2">Corso</th>
                <th className="pb-2 w-24">Durata (h)</th>
                <th className="pb-2 w-32">€ a persona</th>
                <th className="pb-2 w-32">€ a sessione</th>
                <th className="pb-2 w-36">Modalità</th>
                <th className="pb-2 w-32">Edizioni</th>
                <th className="pb-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {corsi.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-gray-400">
                  Nessun corso a catalogo. Aggiungine uno qui sotto.
                </td></tr>
              )}
              {corsi.map((c, i) => (
                <tr key={c.id || `n${i}`} className={`border-b last:border-0 ${c.attivo === false ? 'opacity-50' : ''}`}>
                  <td className="py-1 pr-2">
                    <input className="input py-1 text-sm" placeholder="es. Addetto antincendio rischio medio"
                           value={c.nome || ''} onChange={e => setCorso(i, 'nome', e.target.value)} />
                  </td>
                  <td className="py-1 pr-2">
                    <input className="input py-1 text-sm text-right" type="number" step="0.5"
                           value={c.durata_ore ?? ''} onChange={e => setCorso(i, 'durata_ore', e.target.value)} />
                  </td>
                  <td className="py-1 pr-2">
                    <CampoImporto className="input py-1 text-sm text-right" value={c.prezzo_persona ?? ''}
                                  onChange={v => setCorso(i, 'prezzo_persona', v)} />
                  </td>
                  <td className="py-1 pr-2">
                    <CampoImporto className="input py-1 text-sm text-right" value={c.prezzo_sessione ?? ''}
                                  onChange={v => setCorso(i, 'prezzo_sessione', v)} />
                  </td>
                  <td className="py-1 pr-2">
                    <input className="input py-1 text-sm" placeholder="aula, in azienda…"
                           value={c.modalita || ''} onChange={e => setCorso(i, 'modalita', e.target.value)} />
                  </td>
                  <td className="py-1 pr-2 text-center">
                    {c.id ? (
                      futureDi(c.id) > 0
                        ? <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 whitespace-nowrap">
                            {futureDi(c.id)} in programma
                          </span>
                        : <span className="text-xs text-amber-700">nessuna data</span>
                    ) : <span className="text-xs text-gray-400">da salvare</span>}
                  </td>
                  {/* Un corso già erogato non si butta via: si archivia, così
                      gli abbinamenti e lo storico restano in piedi. */}
                  <td className="py-1 text-right">
                    {c.id ? (
                      <button className={c.attivo === false
                                ? 'text-brand-600 hover:text-brand-800'
                                : 'text-gray-300 hover:text-red-600'}
                              title={c.attivo === false ? 'Rimetti a catalogo' : 'Archivia'}
                              onClick={() => setCorso(i, 'attivo', c.attivo === false)}>
                        {c.attivo === false ? '↺' : '✕'}
                      </button>
                    ) : (
                      <button className="text-gray-300 hover:text-red-600" title="Togli la riga"
                              onClick={() => setCorsi(c2 => c2.filter((_, j) => j !== i))}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary btn-sm"
                  onClick={() => setCorsi(c => [...c, { nome: '', durata_ore: '', prezzo_persona: '',
                                                        prezzo_sessione: '', modalita: '', attivo: true }])}>
            + Corso
          </button>
          <button className="btn-primary btn-sm ml-auto" disabled={salvando} onClick={salvaCorsi}>
            {salvando ? 'Salvo…' : 'Salva i corsi'}
          </button>
        </div>
      </div>

      {/* Il calendario delle edizioni */}
      <div className="card space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Date di erogazione previste
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Quando si tiene ciascun corso. La prima data utile finisce nella comunicazione al
              cliente, accanto al prezzo: è quello che trasforma la proposta in un appuntamento.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" className="w-4 h-4" checked={tutte}
                   onChange={e => setTutte(e.target.checked)} />
            Mostra anche le passate e le annullate
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="pb-2">Corso</th>
                <th className="pb-2 w-36">Data</th>
                <th className="pb-2 w-24">Ora</th>
                <th className="pb-2 w-44">Luogo</th>
                <th className="pb-2 w-20">Posti</th>
                <th className="pb-2 w-32">Stato</th>
                <th className="pb-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {sessioni.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-gray-400">
                  Nessuna edizione in programma.
                </td></tr>
              )}
              {sessioni.map((s, i) => (
                <tr key={s.id || `n${i}`} className="border-b last:border-0">
                  <td className="py-1 pr-2">
                    <select className="input py-1 text-sm" value={s.catalogo_id || ''}
                            onChange={e => setSess(i, 'catalogo_id', e.target.value)}>
                      <option value="">— scegli il corso —</option>
                      {corsi.filter(c => c.id && c.attivo !== false)
                            .map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-2">
                    <input className="input py-1 text-sm" type="date" value={s.data?.slice(0, 10) || ''}
                           onChange={e => setSess(i, 'data', e.target.value)} />
                  </td>
                  <td className="py-1 pr-2">
                    <input className="input py-1 text-sm" placeholder="9–13"
                           value={s.ora || ''} onChange={e => setSess(i, 'ora', e.target.value)} />
                  </td>
                  <td className="py-1 pr-2">
                    <input className="input py-1 text-sm" placeholder="Genova / sede cliente"
                           value={s.luogo || ''} onChange={e => setSess(i, 'luogo', e.target.value)} />
                  </td>
                  <td className="py-1 pr-2">
                    <input className="input py-1 text-sm text-right" type="number" min="0"
                           value={s.posti ?? ''} onChange={e => setSess(i, 'posti', e.target.value)} />
                  </td>
                  <td className="py-1 pr-2">
                    <select className="input py-1 text-sm" value={s.stato || 'prevista'}
                            onChange={e => setSess(i, 'stato', e.target.value)}>
                      {STATI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="py-1 text-right">
                    <button className="text-gray-300 hover:text-red-600"
                            onClick={() => setSessioni(x => x.filter((_, j) => j !== i))}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary btn-sm"
                  onClick={() => setSessioni(s => [...s, { catalogo_id: '', data: oggi(), stato: 'prevista' }])}>
            + Data
          </button>
          <button className="btn-primary btn-sm ml-auto" disabled={salvando} onClick={salvaSessioni}>
            {salvando ? 'Salvo…' : 'Salva le date'}
          </button>
        </div>
      </div>
    </div>
  );
}
